import React, { useState, useEffect } from "react";
import { useDispatch, connect } from "react-redux";
import {
  Row, Col, Card, CardBody, CardTitle, CardHeader, Badge,
} from "reactstrap";
import {
  Table, Thead, Tbody, Tr, Th, Td,
} from "react-super-responsive-table";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import { Link } from "react-router-dom";
import { MetaTags } from "react-meta-tags";
import { withTranslation } from "react-i18next";

import CustomPagination from "components/Common/CustomPagination";
import TableLoader from "components/Common/TableLoader";
import SearchBar from "components/Common/SearchBar";
import formatDate from "helpers/formatDate";
import { fetchEvents } from "store/events/actions";
import EventsFilter from "./EventsFilter";

function EventsList(props) {
  const dispatch = useDispatch();
  const [sizePerPage, setSizePerPage] = useState(10);
  const [searchInputText, setSearchInputText] = useState("");
  const [filteredValues, setFilteredValues] = useState({
    isLead: "",
    eventKey: "",
    customerEmail: "",
    country: "",
    filterDate: {
      fromDate: "",
      toDate: "",
    },
  });

  const columns = [
    {
      dataField: "createdAt",
      text: props.t("Date"),
      formatter: (val) => formatDate(val.createdAt, "DD/MM/YYYY"),
    },
    {
      dataField: "eventName",
      text: props.t("Event"),
    },
    {
      dataField: "name",
      text: props.t("Name"),
      formatter: (val) => {
        const customer = val.customer;
        if (!customer) return "-";
        const base = customer.isLead ? "leads" : "clients";
        return (
          <Link to={`/${base}/${customer._id}/profile`}>
            <strong className="text-capitalize">{customer.firstName} {customer.lastName}</strong>
          </Link>
        );
      },
    },
    {
      dataField: "type",
      text: props.t("Type"),
      formatter: (val) => (
        <Badge color={val.customer?.isLead ? "warning" : "success"}>
          {val.customer?.isLead ? props.t("Lead") : props.t("Client")}
        </Badge>
      ),
    },
    {
      dataField: "email",
      text: props.t("Email"),
      formatter: (val) => val.customer?.email || "-",
    },
    {
      dataField: "phone",
      text: props.t("Phone"),
      formatter: (val) => val.answers?.phone || val.customer?.phone || "-",
    },
    {
      dataField: "city",
      text: props.t("City"),
      formatter: (val) => val.answers?.city || "-",
    },
    {
      dataField: "country",
      text: props.t("Country"),
      formatter: (val) => val.answers?.country || "-",
    },
  ];

  const fetchData = (page, limit) => {
    dispatch(fetchEvents({
      page: page || 1,
      limit: limit || sizePerPage,
      searchText: searchInputText || undefined,
      isLead: filteredValues.isLead || undefined,
      eventKey: filteredValues.eventKey || undefined,
      customerEmail: filteredValues.customerEmail || undefined,
      country: filteredValues.country || undefined,
      fromDate: filteredValues.filterDate?.fromDate || undefined,
      toDate: filteredValues.filterDate?.toDate || undefined,
    }));
  };

  useEffect(() => {
    fetchData(1, sizePerPage);
  }, [sizePerPage, searchInputText, filteredValues]);

  const handleSearchInput = (e) => setSearchInputText(e.target.value);

  return (
    <React.Fragment>
      <MetaTags>
        <title>Events</title>
      </MetaTags>
      <div className="page-content">
        <div className="container-fluid">
          <h2>{props.t("Events")}</h2>
          <Row>
            <Col className="col-12">
              <Card>
                <CardHeader className="d-flex flex-column gap-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <CardTitle className="color-primary">{props.t("Events")} ({props.totalDocs})</CardTitle>
                  </div>
                  <div className="d-flex flex-row align-items-center">
                    <SearchBar handleSearchInput={handleSearchInput} />
                    <EventsFilter
                      filteredValues={filteredValues}
                      filterChangeHandler={setFilteredValues}
                    />
                  </div>
                </CardHeader>
                <CardBody>
                  <div className="table-rep-plugin">
                    <div className="table-responsive mb-0" data-pattern="priority-columns">
                      <Table id="events-table" className="table table-hover">
                        <Thead className="text-center table-light">
                          <Tr>
                            {columns.map((column, index) => (
                              <Th data-priority={index} key={index}>
                                <span className="color-primary">{column.text}</span>
                              </Th>
                            ))}
                          </Tr>
                        </Thead>
                        <Tbody className="text-center" style={{ fontSize: "13px" }}>
                          {props.loading && <TableLoader colSpan={columns.length} />}
                          {!props.loading && props.events.length === 0 && (
                            <Tr>
                              <Td colSpan="100%" className="fw-bolder text-center">
                                <h3 className="fw-bolder text-center">{props.t("No Events Registration")}</h3>
                              </Td>
                            </Tr>
                          )}
                          {!props.loading && props.events.map((row, rowIndex) => (
                            <Tr key={rowIndex}>
                              {columns.map((column, index) => (
                                <Td key={`${rowIndex}-${index}`}>
                                  {column.formatter ? column.formatter(row, rowIndex) : row[column.dataField]}
                                </Td>
                              ))}
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                      <CustomPagination
                        {...props}
                        setSizePerPage={setSizePerPage}
                        sizePerPage={sizePerPage}
                        onChange={fetchData}
                        docs={props.events}
                      />
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
}

const mapStateToProps = (state) => ({
  loading: state.eventsReducer.loading || false,
  events: state.eventsReducer.events?.docs || [],
  page: state.eventsReducer.events?.page || 1,
  totalDocs: state.eventsReducer.events?.totalDocs || 0,
  totalPages: state.eventsReducer.events?.totalPages || 0,
  hasNextPage: state.eventsReducer.events?.hasNextPage,
  hasPrevPage: state.eventsReducer.events?.hasPrevPage,
  limit: state.eventsReducer.events?.limit,
  nextPage: state.eventsReducer.events?.nextPage,
  pagingCounter: state.eventsReducer.events?.pagingCounter,
  prevPage: state.eventsReducer.events?.prevPage,
});

export default connect(mapStateToProps, null)(withTranslation()(EventsList));
